import {
    MobileOfferAssignment,
    MobileOfferAssignmentReadiness,
    MobileOfferAssignmentReadinessStatus,
    MobileOfferAssignmentStatus,
    MobileOfferCatalogEntry,
    ReassignMobileOfferRequest,
} from '@numeo/types'
import { useEffect, useRef, useState } from 'react'
import { mobileOfferError, mobileOffersApi } from './api'
import { isReviewedStoreOfferCurrent } from './assignmentStores'
import { CustomerActionDialog } from './CustomerActionDialog'
import { CustomerAssignmentTerms } from './CustomerAssignmentTerms'
import { CustomerOfferSummary } from './CustomerOfferSummary'
import { findCustomerAssignment } from './customer-assignment-reconciliation'
import { customerDeadlineDefault, customerOfferDate } from './customer-offer-utils'
import { canReassignOffer, clearPendingReassignment, matchesReassignment, readPendingReassignment, savePendingReassignment } from './reassignmentState'
import { OfferButton, OfferNotice } from './ui'

interface CustomerReassignmentDialogProps {
    assignment: MobileOfferAssignment
    /** Draft form values; redeemBy uses the datetime-local input format. Saved retries take precedence. */
    initialTerms?: { redeemBy: string; reason: string }
    onResult: (assignment: MobileOfferAssignment, deliveryConfirmed: boolean) => void
    onClose: () => void
}

export function CustomerReassignmentDialog({ assignment, initialTerms, onResult, onClose }: CustomerReassignmentDialogProps) {
    const [offer, setOffer] = useState<MobileOfferCatalogEntry | null>(null)
    const [redeemBy, setRedeemBy] = useState(() => initialTerms?.redeemBy ?? customerDeadlineDefault())
    const [reason, setReason] = useState(initialTerms?.reason ?? assignment.reason)
    const [pending, setPending] = useState<ReassignMobileOfferRequest | null>(null)
    const [review, setReview] = useState<ReassignMobileOfferRequest | null>(null)
    const [deliveryAssignment, setDeliveryAssignment] = useState<MobileOfferAssignment | null>(null)
    const [availability, setAvailability] = useState<MobileOfferAssignmentReadiness | null>(null)
    const [consent, setConsent] = useState(false)
    const [working, setWorking] = useState(false)
    const [error, setError] = useState('')
    const [storageBlocked, setStorageBlocked] = useState(false)
    const active = useRef(true)
    const sequence = useRef(0)
    const inFlight = useRef(false)
    const deliveryClosed =
        !!deliveryAssignment && (deliveryAssignment.status !== MobileOfferAssignmentStatus.ASSIGNED || Date.parse(deliveryAssignment.redeemBy) <= Date.now())

    useEffect(() => {
        active.current = true
        let cancelled = false
        try {
            setPending(readPendingReassignment(assignment))
        } catch {
            setStorageBlocked(true)
            setError('Saved reassignment details could not be read. Refresh history and contact support before retrying.')
        }
        void mobileOffersApi.find(assignment.offerId).then(
            (record) => {
                if (!cancelled) setOffer(record)
            },
            (failure) => {
                if (!cancelled) setError(mobileOfferError(failure))
            }
        )
        return () => {
            active.current = false
            cancelled = true
        }
    }, [assignment])

    const invalidate = () => {
        sequence.current++
        inFlight.current = false
        setWorking(false)
        setReview(null)
        setConsent(false)
        setAvailability(null)
        setError('')
    }

    const recover = (record: MobileOfferAssignment) => {
        setDeliveryAssignment(record)
        setPending(null)
        setReview(null)
        setConsent(false)
        setAvailability(null)
        // A matching new revision proves the enrollment was saved, not that code delivery succeeded.
        onResult(record, false)
        try {
            clearPendingReassignment(assignment)
        } catch {
            /* Retained state can be reconciled again; never issue another reassignment to clear it. */
        }
    }

    const reconcile = async (request: ReassignMobileOfferRequest, isCurrent: () => boolean): Promise<boolean> => {
        const observed = await findCustomerAssignment(mobileOffersApi.history, assignment.userId, (record) => record.id === assignment.id)
        if (!isCurrent()) return true
        if (observed && matchesReassignment(observed, assignment, request)) {
            recover(observed)
            return true
        }
        if (!observed || (observed.revision ?? 0) !== request.expectedRevision || !canReassignOffer(observed)) {
            setError('The saved request could not be matched to the current assignment. Refresh history before retrying.')
            return true
        }
        return false
    }

    const checkReadiness = (request: ReassignMobileOfferRequest) =>
        deliveryAssignment
            ? mobileOffersApi.assignmentReadiness({
                  offerId: deliveryAssignment.offerId,
                  userId: deliveryAssignment.userId,
                  provider: deliveryAssignment.provider,
                  redeemBy: deliveryAssignment.redeemBy,
                  expectedRevision: deliveryAssignment.revision ?? 0,
              })
            : mobileOffersApi.reassignReadiness(assignment.id, { expectedRevision: request.expectedRevision, redeemBy: request.redeemBy })

    const prepare = async () => {
        if (inFlight.current || !offer || storageBlocked || deliveryClosed) return
        const deadline = deliveryAssignment?.redeemBy || pending?.redeemBy || redeemBy
        const nextReason = deliveryAssignment?.reason || pending?.reason || reason.trim()
        if (!pending && (!Number.isFinite(Date.parse(deadline)) || Date.parse(deadline) <= Date.now())) {
            setError('Choose a redemption deadline in the future.')
            return
        }
        if (!nextReason || nextReason.length > 500) {
            setError('Enter a reason of up to 500 characters.')
            return
        }
        const request = pending || {
            expectedRevision: (deliveryAssignment || assignment).revision ?? 0,
            requestId: crypto.randomUUID(),
            redeemBy: new Date(deadline).toISOString(),
            reason: nextReason,
        }
        const current = ++sequence.current
        const isCurrent = () => active.current && current === sequence.current
        inFlight.current = true
        setWorking(true)
        setError('')
        setAvailability(null)
        setConsent(false)
        try {
            if (pending && (await reconcile(request, isCurrent))) return
            if (!isCurrent()) return
            if (Date.parse(request.redeemBy) <= Date.now()) {
                setError(
                    'This saved request has expired and history does not confirm a new revision. Its outcome needs administrator review; no new request was sent.'
                )
                return
            }
            const result = await checkReadiness(request)
            if (!isCurrent()) return
            setAvailability(result)
            if (result.status === MobileOfferAssignmentReadinessStatus.READY) setReview(request)
        } catch (failure) {
            if (isCurrent()) setError(mobileOfferError(failure))
        } finally {
            if (isCurrent()) {
                inFlight.current = false
                setWorking(false)
            }
        }
    }

    const confirm = async () => {
        if (!review || !consent || !offer || inFlight.current || storageBlocked) return
        const current = ++sequence.current
        const isCurrent = () => active.current && current === sequence.current
        inFlight.current = true
        setWorking(true)
        setError('')
        let submitted = false
        try {
            const currentOffer = await mobileOffersApi.find(assignment.offerId)
            if (!isCurrent()) return
            if (!isReviewedStoreOfferCurrent(currentOffer, offer, 'apple')) {
                setOffer(currentOffer)
                setReview(null)
                setConsent(false)
                setError('The offer configuration changed. Review the current terms again.')
                return
            }
            const result = await checkReadiness(review)
            if (!isCurrent()) return
            setAvailability(result)
            if (result.status !== MobileOfferAssignmentReadinessStatus.READY) {
                setReview(null)
                setConsent(false)
                return
            }
            if (Date.parse(review.redeemBy) <= Date.now()) {
                setReview(null)
                setError('The redemption deadline has passed. Refresh the assignment before continuing.')
                return
            }
            if (!deliveryAssignment) {
                try {
                    savePendingReassignment(assignment, review)
                } catch {
                    setStorageBlocked(true)
                    setError('The reassignment request could not be saved in this browser. No request was sent.')
                    return
                }
                setPending(review)
            }
            submitted = true
            const record = deliveryAssignment
                ? await mobileOffersApi.assign({
                      offerId: deliveryAssignment.offerId,
                      userId: deliveryAssignment.userId,
                      provider: deliveryAssignment.provider,
                      redeemBy: deliveryAssignment.redeemBy,
                      reason: deliveryAssignment.reason,
                      expectedRevision: deliveryAssignment.revision ?? 0,
                  })
                : await mobileOffersApi.reassign(assignment.id, review)
            if (!isCurrent()) return
            const matches = deliveryAssignment
                ? record.id === deliveryAssignment.id &&
                  (record.revision ?? 0) === (deliveryAssignment.revision ?? 0) &&
                  record.redeemBy === deliveryAssignment.redeemBy &&
                  record.reason === deliveryAssignment.reason &&
                  record.status === 'assigned'
                : matchesReassignment(record, assignment, review) && record.status === MobileOfferAssignmentStatus.ASSIGNED
            if (!matches) throw new Error('Unexpected reassignment response')
            try {
                clearPendingReassignment(assignment)
            } catch {
                /* The successful response is authoritative; retained request state remains safe to reconcile. */
            }
            onResult(record, true)
            onClose()
        } catch (failure) {
            if (!isCurrent()) return
            setConsent(false)
            setReview(null)
            if ((submitted || pending) && !deliveryAssignment) {
                try {
                    if (await reconcile(review, isCurrent)) return
                } catch {
                    /* Keep the saved request identity when history is also unavailable. */
                }
            }
            if (isCurrent())
                setError(
                    submitted ? `${mobileOfferError(failure)} Delivery is not confirmed. Review the saved request before retrying.` : mobileOfferError(failure)
                )
        } finally {
            if (isCurrent()) {
                inFlight.current = false
                setWorking(false)
            }
        }
    }

    return (
        <CustomerActionDialog title={review ? 'Review reassignment' : 'Reassign offer'} busy={working} onClose={onClose}>
            <div className="space-y-4">
                {offer ? <CustomerOfferSummary offer={offer} provider="apple" /> : <OfferNotice>Loading offer…</OfferNotice>}
                <OfferNotice>
                    Reassignment opens a new redemption window for an unused App Store offer. Previous terms stay in history. No subscription is activated.
                </OfferNotice>
                {deliveryAssignment ? (
                    <OfferNotice>
                        {deliveryClosed
                            ? `Reassignment revision ${deliveryAssignment.revision ?? 0} was saved, but its redemption window is closed (${deliveryAssignment.status}). Close this dialog and review current history; no delivery retry was sent.`
                            : `Reassignment revision ${deliveryAssignment.revision ?? 0} was saved, but code delivery is not confirmed. Retry delivery using its saved terms.`}
                    </OfferNotice>
                ) : pending ? (
                    <OfferNotice>A previous reassignment request is saved. Its deadline and reason are locked until the outcome is confirmed.</OfferNotice>
                ) : null}
                {review || pending || deliveryAssignment ? (
                    <dl className="space-y-2 text-sm">
                        <div>
                            <dt>Redeem before · {Intl.DateTimeFormat().resolvedOptions().timeZone}</dt>
                            <dd className="font-medium">{customerOfferDate((review || deliveryAssignment || pending)!.redeemBy)}</dd>
                        </div>
                        <div>
                            <dt>Assignment reason</dt>
                            <dd className="whitespace-pre-wrap break-words">{(review || deliveryAssignment || pending)!.reason}</dd>
                        </div>
                    </dl>
                ) : (
                    <fieldset disabled={working} className="space-y-4">
                        <CustomerAssignmentTerms
                            redeemBy={redeemBy}
                            reason={reason}
                            onDeadline={(value) => {
                                invalidate()
                                setRedeemBy(value)
                            }}
                            onReason={(value) => {
                                invalidate()
                                setReason(value)
                            }}
                        />
                    </fieldset>
                )}
                {availability && availability.status !== MobileOfferAssignmentReadinessStatus.READY && (
                    <OfferNotice error>
                        {availability.message}
                        {availability.readyAfter && ` Check again after ${customerOfferDate(availability.readyAfter)}.`}
                    </OfferNotice>
                )}
                {error && <OfferNotice error>{error}</OfferNotice>}
                {review && (
                    <label className="flex items-start gap-3 text-sm">
                        <input type="checkbox" className="mt-1" checked={consent} disabled={working} onChange={(event) => setConsent(event.target.checked)} />
                        <span>I approve these redemption terms for this customer’s App Store offer.</span>
                    </label>
                )}
                <div className="flex flex-wrap justify-end gap-3">
                    <OfferButton variant="secondary" disabled={working} onClick={onClose}>
                        Close
                    </OfferButton>
                    {review && !pending && !deliveryAssignment && (
                        <OfferButton variant="secondary" disabled={working} onClick={invalidate}>
                            Back to edit
                        </OfferButton>
                    )}
                    <OfferButton
                        disabled={working || !offer || storageBlocked || deliveryClosed || (!!review && !consent)}
                        onClick={() => void (review ? confirm() : prepare())}
                    >
                        {working
                            ? 'Checking…'
                            : review
                              ? deliveryAssignment
                                  ? 'Confirm code delivery'
                                  : 'Confirm reassignment'
                              : deliveryAssignment
                                ? 'Review code delivery'
                                : pending
                                  ? 'Review saved reassignment'
                                  : 'Review reassignment'}
                    </OfferButton>
                </div>
            </div>
        </CustomerActionDialog>
    )
}
